import { useState, useEffect } from 'react';

interface Student {
  id: string;
  name: string;
  email: string;
  course_id: string;
  course_title: string;
  progress: number;
  enrolled_at: string;
  last_activity?: string;
}

export default function Students({ userProfile }: { userProfile: any }) {
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCourse, setSelectedCourse] = useState<string>('all');
  const [courses, setCourses] = useState<any[]>([]);

  // Fetch students and courses data
  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      // First, fetch teacher's courses using teacher_id
      const coursesResponse = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-courses`, {
        method: 'GET',
        headers: {
          'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
          'Content-Type': 'application/json',
        },
      });

      if (coursesResponse.ok) {
        const allCourses = await coursesResponse.json();
        // Filter courses by teacher_id
        const coursesData = allCourses.filter((c: any) => c.teacher_id === userProfile?.id);
        setCourses(coursesData || []);
        
        console.log('Teacher courses:', coursesData);
        
        // Get course IDs for this teacher
        const teacherCourseIds = coursesData.map((c: any) => c.id);
        
        if (teacherCourseIds.length === 0) {
          setStudents([]);
          setLoading(false);
          return;
        }

        // Fetch enrollments for teacher's courses
        const enrollmentsPromises = teacherCourseIds.map(async (courseId: string) => {
          const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-enrollments?course_id=${courseId}`, {
            method: 'GET',
            headers: {
              'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
              'Content-Type': 'application/json',
            },
          });
          
          if (response.ok) {
            const data = await response.json();
            return data.map((enrollment: any) => ({
              ...enrollment,
              course_id: courseId
            }));
          }
          return [];
        });

        const allEnrollments = await Promise.all(enrollmentsPromises);
        const flatEnrollments = allEnrollments.flat();

        console.log('All enrollments:', flatEnrollments);

        // Fetch student profiles for each enrollment
        const studentsDataPromises = flatEnrollments.map(async (enrollment: any) => {
          try {
            const profileResponse = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-profile?user_id=${enrollment.student_id}`, {
              method: 'GET',
              headers: {
                'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
                'Content-Type': 'application/json',
              },
            });

            let studentName = 'Học sinh';
            let studentEmail = '';
            
            if (profileResponse.ok) {
              const profile = await profileResponse.json();
              studentName = profile.full_name || profile.email || 'Học sinh';
              studentEmail = profile.email || '';
            }

            return {
              id: enrollment.student_id,
              name: studentName,
              email: studentEmail,
              course_id: enrollment.course_id,
              course_title: coursesData.find((c: any) => c.id === enrollment.course_id)?.title || 'Khóa học',
              progress: enrollment.progress || 0,
              enrolled_at: enrollment.enrolled_at,
              last_activity: enrollment.last_activity
            };
          } catch (err) {
            console.error('Error fetching student profile:', err);
            return {
              id: enrollment.student_id,
              name: 'Học sinh',
              email: '',
              course_id: enrollment.course_id,
              course_title: coursesData.find((c: any) => c.id === enrollment.course_id)?.title || 'Khóa học',
              progress: enrollment.progress || 0,
              enrolled_at: enrollment.enrolled_at,
              last_activity: enrollment.last_activity
            };
          }
        });

        const studentsData = await Promise.all(studentsDataPromises);
        console.log('Students data:', studentsData);
        setStudents(studentsData);
      } else {
        throw new Error('Không thể tải dữ liệu khóa học');
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Không thể tải dữ liệu học sinh. Vui lòng thử lại sau.');
      setStudents([]);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userProfile?.id) {
      fetchData();
    }
  }, [userProfile]);

  // Filter students by selected course
  const filteredStudents = selectedCourse === 'all' 
    ? students 
    : students.filter(student => student.course_id === selectedCourse);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-2 text-gray-500">
          <i className="ri-loader-4-line animate-spin"></i>
          <span>Đang tải dữ liệu học sinh...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        <div className="flex items-center">
          <i className="ri-error-warning-line mr-2"></i>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Học sinh của tôi</h2>
        <button
          onClick={fetchData}
          className="flex items-center px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-all whitespace-nowrap"
        >
          <i className="ri-refresh-line mr-2"></i>
          Làm mới
        </button>
      </div>

      {courses.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <i className="ri-book-line text-4xl text-gray-300 mb-4"></i>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Chưa có khóa học nào</h3>
          <p className="text-gray-500">Admin sẽ phân công khóa học cho bạn để quản lý học sinh</p>
        </div>
      ) : (
        <>
          {/* Course Filter */}
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700">Lọc theo khóa học:</label>
            <select
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
            >
              <option value="all">Tất cả khóa học ({students.length} học sinh)</option>
              {courses.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.title} ({students.filter(s => s.course_id === course.id).length} học sinh)
                </option>
              ))}
            </select>
          </div>

          {/* Students Table */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {filteredStudents.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                <i className="ri-user-line text-4xl mb-3"></i>
                <p className="text-sm">
                  {selectedCourse === 'all' 
                    ? 'Chưa có học sinh nào trong các khóa học của bạn' 
                    : 'Chưa có học sinh nào trong khóa học này'
                  }
                </p>
                <p className="text-xs mt-1">Học sinh sẽ hiển thị khi họ đăng ký khóa học</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Học sinh
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Khóa học
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tiến độ
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ngày đăng ký
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Thao tác
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredStudents.map((student, index) => (
                      <tr key={`${student.id}_${student.course_id}_${index}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <i className="ri-user-line text-blue-600"></i>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{student.name}</div>
                              <div className="text-sm text-gray-500">{student.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {student.course_title}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-16 bg-gray-200 rounded-full h-2 mr-3">
                              <div 
                                className="bg-blue-600 h-2 rounded-full transition-all"
                                style={{ width: `${student.progress}%` }}
                              ></div>
                            </div>
                            <span className="text-sm text-gray-600">{student.progress}%</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(student.enrolled_at).toLocaleDateString('vi-VN')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button className="text-blue-600 hover:text-blue-900 mr-3">
                            <i className="ri-eye-line"></i>
                          </button>
                          <button className="text-green-600 hover:text-green-900">
                            <i className="ri-message-line"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Summary Stats */}
          {filteredStudents.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <i className="ri-user-line text-blue-600"></i>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-600">Tổng học sinh</p>
                    <p className="text-xl font-bold text-gray-900">{filteredStudents.length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <i className="ri-check-line text-green-600"></i>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-600">Hoàn thành</p>
                    <p className="text-xl font-bold text-gray-900">
                      {filteredStudents.filter(s => s.progress === 100).length}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <i className="ri-bar-chart-line text-yellow-600"></i>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-600">Tiến độ TB</p>
                    <p className="text-xl font-bold text-gray-900">
                      {filteredStudents.length > 0 
                        ? Math.round(filteredStudents.reduce((sum, s) => sum + s.progress, 0) / filteredStudents.length)
                        : 0}%
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <i className="ri-book-line text-purple-600"></i>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-600">Khóa học</p>
                    <p className="text-xl font-bold text-gray-900">{courses.length}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
